import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { searchTestResultsAsync } from '../../redux/testHistory/testHistorySlice';
import { getDevicesAsync, selectDevices } from '../../redux/devices/getDevicesSlice';
import {
  getRegressionTestIdsAsync,
  selectRegressionTestIds,
  selectRegressionTestIdsLoading,
} from '../../redux/regressionTestIds/getRegressionTestIdsSlice';
import { getMarketsAsync, selectMarkets } from '../../redux/markets/getMarketsSlice';

/**
 * TestHistorySearch - Search form for finding saved test results
 * Displays in the left panel sidebar
 */
export default function TestHistorySearch() {
  const dispatch = useDispatch();
  const allDevices = useSelector(selectDevices) || [];
  const markets = useSelector(selectMarkets) || [];
  const regressionTestIds = useSelector(selectRegressionTestIds);
  const loadingIds = useSelector(selectRegressionTestIdsLoading);

  const [formData, setFormData] = useState({
    regressionTestId: '',
    marketId: '',
    userEmail: '',
    deviceId: '',
  });

  const [errors, setErrors] = useState({});

  // Filter devices by selected market
  const filteredDevices = useMemo(() => {
    if (!formData.marketId) {
      return [];
    }
    const id = Number(formData.marketId);
    return allDevices.filter((device) => Number(device.market_id) === id);
  }, [allDevices, formData.marketId]);

  // Fetch devices, markets, and regression test IDs on mount
  useEffect(() => {
    dispatch(getDevicesAsync());
    dispatch(getMarketsAsync());
    dispatch(getRegressionTestIdsAsync());
  }, [dispatch]);

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;

    setFormData((prev) => {
      const updated = { ...prev, [field]: newValue };

      // If market changed, check if current device is still valid for the new market
      if (field === 'marketId' && prev.deviceId) {
        if (newValue) {
          const id = Number(newValue);
          const deviceStillValid = allDevices.some(
            (d) => d.id === prev.deviceId && Number(d.market_id) === id,
          );
          if (!deviceStillValid) {
            updated.deviceId = ''; // Clear invalid device selection
          }
        } else {
          // No market selected, clear device
          updated.deviceId = '';
        }
      }

      return updated;
    });

    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Validation - at least one parameter must be provided
    const newErrors = {};
    const hasAtLeastOne =
      formData.regressionTestId || formData.marketId || formData.userEmail || formData.deviceId;

    if (!hasAtLeastOne) {
      newErrors.general = 'Please provide at least one search parameter';
      setErrors(newErrors);
      return;
    }

    // Build filters object with API parameter names (exclude empty optional fields)
    const filters = {};

    if (formData.regressionTestId && formData.regressionTestId.trim()) {
      filters.regression_test_id = formData.regressionTestId.trim();
    }
    if (formData.marketId) {
      const market = markets.find((m) => Number(m.id) === Number(formData.marketId));
      filters.market_id = Number(formData.marketId);
      filters.market_name = market?.name; // For display in export dialog
    }
    if (formData.userEmail && formData.userEmail.trim()) {
      filters.tester_email = formData.userEmail.trim();
    }
    if (formData.deviceId) {
      const device = allDevices.find((d) => d.id === formData.deviceId);
      filters.device_id = formData.deviceId;
      filters.device_name = device?.name; // For display in export dialog
    }

    dispatch(searchTestResultsAsync(filters));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
      {/* Regression Test ID - Autocomplete */}
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        options={regressionTestIds}
        value={formData.regressionTestId}
        onChange={(event, newValue) => {
          setFormData((prev) => ({ ...prev, regressionTestId: newValue || '' }));
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            setFormData((prev) => ({ ...prev, regressionTestId: newInputValue }));
          }
        }}
        loading={loadingIds}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Regression Test ID"
            placeholder="e.g., reg-test-001"
            error={!!errors.regressionTestId}
            helperText={errors.regressionTestId}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingIds ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          />
        )}
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      />

      {/* Market - Dropdown */}
      <FormControl
        fullWidth
        size="small"
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'text.secondary',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'primary.main',
          },
        }}
      >
        <InputLabel id="market-select-label">Market</InputLabel>
        <Select
          labelId="market-select-label"
          value={formData.marketId}
          label="Market"
          onChange={handleChange('marketId')}
        >
          <MenuItem value="">None</MenuItem>
          {markets.map((market) => (
            <MenuItem key={market.id} value={market.id}>
              {market.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* User Email - Optional */}
      <TextField
        fullWidth
        size="small"
        label="User Email"
        type="email"
        value={formData.userEmail}
        onChange={handleChange('userEmail')}
        placeholder="e.g., user@example.com"
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'text.secondary',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'primary.main',
          },
        }}
      />

      {/* Device - Dropdown (disabled until market is selected) */}
      <FormControl
        fullWidth
        size="small"
        disabled={!formData.marketId}
        sx={{
          mb: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'text.secondary',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'primary.main',
          },
        }}
      >
        <InputLabel id="device-select-label">
          {formData.marketId ? 'Device' : 'Device (select market first)'}
        </InputLabel>
        <Select
          labelId="device-select-label"
          value={formData.deviceId}
          label={formData.marketId ? 'Device' : 'Device (select market first)'}
          onChange={handleChange('deviceId')}
        >
          <MenuItem value="">None</MenuItem>
          {filteredDevices.map((device) => (
            <MenuItem key={device.id} value={device.id}>
              {device.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Search Button */}
      <Button
        fullWidth
        type="submit"
        variant="contained"
        startIcon={<SearchIcon />}
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 'bold',
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { bgcolor: 'primary.light' },
          '&:disabled': { bgcolor: 'action.disabledBackground' },
        }}
      >
        Search
      </Button>

      {/* General error message */}
      {errors.general && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.general}
        </Alert>
      )}
    </Box>
  );
}
